/**
 * @file cookies.ts
 * @description Utilidad para gestionar cookies de la aplicación
 * @author XiaVan Team
 *
 * INVENTARIO DE COOKIES USADAS EN LA APLICACIÓN:
 * ================================================================
 *
 * 1. xiavan_mode
 *    - Descripción: Modo de color (claro u oscuro)
 *    - Valores: "light" | "dark"
 *    - Expiración: 1 año
 *    - Usado en: useTheme hook, Footer
 *
 * Nota: Theme es fijo (default-fullwidth), no necesita cookie
 *
 * 2. xiavan_user_prefs (Futuro)
 *    - Descripción: Preferencias de usuario (idioma, tamaño fuente, etc)
 *    - Valores: JSON string
 *    - Expiración: 1 año
 *
 * 3. xiavan_auth_token (Futuro)
 *    - Descripción: Token JWT de autenticación
 *    - Valores: JWT string
 *    - Expiración: 7 días
 *    - Usado en: useAuth hook
 *
 * 4. xiavan_favorites (Futuro - puede ir a localStorage)
 *    - Descripción: IDs de planes favoritos (fallback si API falla)
 *    - Valores: JSON string array
 *    - Expiración: 30 días
 *
 * ================================================================
 */

interface CookieOptions {
  maxAge?: number // Segundos
  path?: string
  domain?: string
  secure?: boolean
  sameSite?: 'Strict' | 'Lax' | 'None'
}

/**
 * Obtener valor de una cookie
 * @param name - Nombre de la cookie
 * @returns Valor de la cookie o null
 */
export function getCookie(name: string): string | null {
  const nameEQ = `${name}=`
  const cookies = document.cookie.split(';')

  for (let cookie of cookies) {
    cookie = cookie.trim()
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length))
    }
  }

  return null
}

/**
 * Establecer una cookie
 * @param name - Nombre de la cookie
 * @param value - Valor a guardar
 * @param options - Opciones (expiración, path, etc)
 */
export function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {}
): void {
  let cookieString = `${name}=${encodeURIComponent(value)}`

  if (options.maxAge !== undefined) {
    cookieString += `; max-age=${options.maxAge}`
  }

  if (options.path !== undefined) {
    cookieString += `; path=${options.path}`
  } else {
    cookieString += '; path=/'
  }

  if (options.domain !== undefined) {
    cookieString += `; domain=${options.domain}`
  }

  if (options.secure) {
    cookieString += '; secure'
  }

  if (options.sameSite !== undefined) {
    cookieString += `; samesite=${options.sameSite}`
  }

  document.cookie = cookieString
}

/**
 * Eliminar una cookie
 * @param name - Nombre de la cookie a eliminar
 */
export function deleteCookie(name: string): void {
  setCookie(name, '', { maxAge: -1 })
}

/**
 * ================================================================
 * FUNCIONES ESPECÍFICAS PARA GESTIÓN DE MODO (LIGHT/DARK)
 * ================================================================
 */

const MODE_COOKIE = 'xiavan_mode'

// Expiración por defecto: 1 año en segundos
const DEFAULT_EXPIRATION = 365 * 24 * 60 * 60

/**
 * Obtener modo actual desde cookie
 * @returns Modo actual (light por defecto)
 */
export function getMode(): 'light' | 'dark' {
  const mode = getCookie(MODE_COOKIE) || 'light'
  return mode as 'light' | 'dark'
}

/**
 * Guardar modo en cookie
 * @param mode - 'light' o 'dark'
 */
export function setMode(mode: 'light' | 'dark'): void {
  setCookie(MODE_COOKIE, mode, {
    maxAge: DEFAULT_EXPIRATION,
    sameSite: 'Lax',
  })
}

/**
 * Verificar si el sistema prefiere dark mode
 * @returns true si el SO/navegador prefiere dark mode
 */
export function prefersDarkMode(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

/**
 * Inicializar modo desde preferencias del sistema (si no hay cookie)
 * Útil para primera visita del usuario
 */
export function initializeModeFromSystem(): 'light' | 'dark' {
  const savedMode = getCookie(MODE_COOKIE)
  if (savedMode) {
    return savedMode as 'light' | 'dark'
  }

  const mode = prefersDarkMode() ? 'dark' : 'light'
  setMode(mode)
  return mode
}
